import { useEffect, useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";

function formatCommentTimestamp(value) {
  if (!value) return "Unknown";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-GB");
}

export default function CommentsPanel({
  expanded,
  leadContext,
  organizationName,
  getApiJson,
  postJson,
}) {
  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentsError, setCommentsError] = useState("");
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [isCommentSaving, setIsCommentSaving] = useState(false);
  const commentsEndRef = useRef(null);

  useEffect(() => {
    if (!expanded || !getApiJson || !leadContext || !organizationName) return;

    let isActive = true;

    const run = async () => {
      setCommentsLoading(true);
      setCommentsError("");

      try {
        const nextComments = await getApiJson(
          `/api/leads/lead-context/${leadContext}/company/${encodeURIComponent(
            organizationName
          )}/comments`
        );
        if (isActive) {
          setComments(Array.isArray(nextComments) ? nextComments : []);
        }
      } catch (error) {
        console.error("Unable to load comments:", error);
        if (isActive) {
          setCommentsError("Unable to load team comments.");
        }
      } finally {
        if (isActive) {
          setCommentsLoading(false);
        }
      }
    };

    run();

    return () => {
      isActive = false;
    };
  }, [expanded, getApiJson, leadContext, organizationName]);

  useEffect(() => {
    if (!expanded) return;

    commentsEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [comments, expanded]);

  const saveComment = async () => {
    if (!postJson || !leadContext || !organizationName) return;

    const trimmedComment = commentDraft.trim();
    if (!trimmedComment) {
      setCommentsError("Comment message is required.");
      return;
    }

    setIsCommentSaving(true);
    setCommentsError("");

    try {
      const savedComment = await postJson(
        `/api/leads/lead-context/${leadContext}/company/${encodeURIComponent(
          organizationName
        )}/comments`,
        {
          message: trimmedComment,
        }
      );
      setComments((prev) => [...prev, savedComment]);
      setCommentDraft("");
    } catch (error) {
      console.error("Unable to save comment:", error);
      setCommentsError("Unable to save team comment.");
    } finally {
      setIsCommentSaving(false);
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: 2,
        backgroundColor: "#ffffff",
        overflow: "hidden",
        minHeight: 340,
        display: "grid",
        gridTemplateRows: "auto minmax(160px, 1fr) auto",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
          backgroundColor: "#f8fafc",
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>Team Comments</Typography>
        <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
          Shared notes for this company. Latest comment appears at the bottom.
        </Typography>
      </Box>

      <Box
        sx={{
          px: 1.25,
          py: 1.25,
          display: "grid",
          gap: 1,
          maxHeight: 280,
          overflowY: "auto",
          alignContent: comments.length === 0 ? "center" : "start",
          backgroundColor: "#f8fafc",
        }}
      >
        {commentsLoading ? (
          <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>
            Loading team comments...
          </Typography>
        ) : comments.length === 0 ? (
          <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>
            No comments yet.
          </Typography>
        ) : (
          comments.map((comment) => (
            <Box
              key={comment.id}
              sx={{
                border: "1px solid rgba(15, 23, 42, 0.08)",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                px: 1.1,
                py: 0.9,
                display: "grid",
                gap: 0.65,
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
                  {comment.userId}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                  {formatCommentTimestamp(comment.createdAt)}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: "0.92rem",
                  color: "#1e293b",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {comment.message}
              </Typography>
            </Box>
          ))
        )}
        <Box ref={commentsEndRef} />
      </Box>

      <Box
        sx={{
          px: 1.25,
          py: 1.25,
          borderTop: "1px solid rgba(15, 23, 42, 0.08)",
          display: "grid",
          gap: 1,
        }}
      >
        <TextField
          label="Add team comment"
          multiline
          fullWidth
          rows={3}
          value={commentDraft}
          onChange={(event) => {
            setCommentDraft(event.target.value);
            if (commentsError) {
              setCommentsError("");
            }
          }}
        />

        {commentsError && (
          <Typography sx={{ fontSize: "0.85rem", color: "#b91c1c" }}>
            {commentsError}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={saveComment}
            disabled={isCommentSaving}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {isCommentSaving ? "Saving..." : "Add Comment"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
